import React, {
  useState,
  useRef,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import dropDown from "../assets/svg/dropDown.svg";

// Define the props interface for THIS component

// backgroundContent
// i wan tot change backgroundContent width base on slide dont use rigth becuse i wnat to user container quary of tailwind

interface OverlaySliderProps {
  children: ReactNode[]; // Expecting [backgroundContent, overlayContent]
  initialOverlayWidth?: string; // Initial width for the overlay panel (e.g., '500px', '40%')
  minOverlayWidth?: number; // Minimum pixel width for the overlay panel
  maxOverlayWidthPercent?: number; // Optional: Max width as % of container (e.g., 90)
  sliderWidth?: number; // Width of the draggable slider handle in pixels
  sliderColor?: string; // Color of the slider handle
  overlayBgColor?: string; // Background color for the overlay panel
  overlayShadow?: string; // Optional shadow for the overlay panel
  onSlide?: (overlayWidthPx: number, overlayWidthRatio: number) => void; // Callback
  zIndexBase?: number; // Base z-index for overlay elements
}

const OverlaySlider: React.FC<OverlaySliderProps> = ({
  children,
  initialOverlayWidth = "500px", // Default initial overlay width
  minOverlayWidth = 50, // Default minimum overlay width
  maxOverlayWidthPercent, // No default max percentage limit
  sliderWidth = 8, // Default slider width
  sliderColor = "#9ca3af", // Default gray-400 slider
  overlayBgColor = "white", // Default overlay background
  overlayShadow = "0 0 15px rgba(0, 0, 0, 0.2)", // Default shadow
  onSlide,
  zIndexBase = 20, // Default z-index
}) => {
  // State for overlay width in pixels
  const [overlayWidth, setOverlayWidth] = useState<number | null>(null);
  // const [isResizing, setIsResizing] = useState<boolean>(false); // Keep if needed for styles/effects
  const isResizingRef = useRef<boolean>(false); // Ref to track resizing status reliably for listeners

  // Ref for the main container
  const containerRef = useRef<HTMLDivElement>(null);

  // Get the two children
  const [backgroundContent, overlayContent] = React.Children.toArray(children);

  // --- Initialization: Calculate initial overlay width ---
  useEffect(() => {
    // Run only once after mount and if width hasn't been set
    if (containerRef.current && overlayWidth === null) {
      const containerWidth = containerRef.current.offsetWidth;
      let initialWidthPx: number;

      // Calculate initial pixel width from string prop
      if (initialOverlayWidth.endsWith("%")) {
        const percentage = parseFloat(initialOverlayWidth) / 100;
        initialWidthPx = containerWidth * percentage;
      } else {
        initialWidthPx = parseFloat(initialOverlayWidth) || 500; // Fallback
      }

      // Apply min constraint
      const safeMinWidth = minOverlayWidth ?? 50; // Use nullish coalescing
      initialWidthPx = Math.max(safeMinWidth, initialWidthPx);

      // Apply max constraint (if provided)
      const maxWidthPx =
        maxOverlayWidthPercent != null // Check if defined
          ? (containerWidth * Math.min(100, maxOverlayWidthPercent)) / 100 // Ensure percent <= 100
          : containerWidth; // Default max is full container width
      initialWidthPx = Math.min(maxWidthPx, initialWidthPx);

      setOverlayWidth(initialWidthPx);
    }
    // Only depend on props that influence initial calculation.
    // overlayWidth dependency removed to ensure it truly runs only once initially.
    // containerRef.current is not added as a dependency because changing the ref itself
    // doesn't require recalculation, only the *existence* of the ref. The initial calc
    // only happens when overlayWidth is null, ensuring it uses the initial container width.
  }, [initialOverlayWidth, minOverlayWidth, maxOverlayWidthPercent]);

  // --- Drag Handlers ---
  const handleMouseMove = useCallback(
    (clientX: number) => {
      // Use the ref here!
      if (!isResizingRef.current || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      // Width is distance from right edge of container to cursor
      let newWidth = containerRect.right - clientX;

      // Apply min constraint
      const safeMinWidth = minOverlayWidth ?? 50; // Use nullish coalescing
      newWidth = Math.max(safeMinWidth, newWidth);

      // Apply max constraint
      const maxWidthPx =
        maxOverlayWidthPercent != null
          ? (containerRect.width * Math.min(100, maxOverlayWidthPercent)) / 100
          : containerRect.width;
      newWidth = Math.min(maxWidthPx, newWidth);

      setOverlayWidth(newWidth);
      if (onSlide) {
        onSlide(newWidth, newWidth / containerRect.width);
      }
      // Keep dependencies that the *logic* inside uses
    },
    [minOverlayWidth, maxOverlayWidthPercent, onSlide]
  );

  // --- Global Event Listener Wrappers ---
  const handleGlobalMouseMove = useCallback(
    (e: MouseEvent) => {
      handleMouseMove(e.clientX);
    },
    [handleMouseMove]
  );

  const handleGlobalTouchMove = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleMouseMove(e.touches[0].clientX);
      }
    },
    [handleMouseMove]
  );

  const handleMouseUp = useCallback(() => {
    // Use the ref here!
    if (isResizingRef.current) {
      isResizingRef.current = false;
      // setIsResizing(false); // Update state if needed elsewhere
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      // Cleanup listeners
      window.removeEventListener("mousemove", handleGlobalMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleGlobalTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
    }
    // Add the handlers to dependency array as they are used for removal
  }, [handleGlobalMouseMove, handleGlobalTouchMove]); // handleMouseUp is implicitly included via useCallback

  // --- Mouse/Touch Down Handler on Slider ---
  const handleMouseDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation(); // Prevent events on underlying elements

      // Set the ref immediately
      isResizingRef.current = true;
      // setIsResizing(true); // Update state if needed elsewhere

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      // Add listeners
      window.addEventListener("mousemove", handleGlobalMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleGlobalTouchMove, {
        passive: false,
      });
      window.addEventListener("touchend", handleMouseUp);
      // Add the handlers to dependency array as they are used for adding/removal
    },
    [handleGlobalMouseMove, handleMouseUp, handleGlobalTouchMove]
  );

  // --- Cleanup Effect ---
  useEffect(() => {
    // Cleanup listeners if component unmounts while resizing
    return () => {
      // Check the ref
      if (isResizingRef.current) {
        window.removeEventListener("mousemove", handleGlobalMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("touchmove", handleGlobalTouchMove);
        window.removeEventListener("touchend", handleMouseUp);
        // Try resetting body styles, though might not be strictly necessary on unmount
        try {
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
        } catch (e) {
          // Ignore potential errors if document/body isn't accessible during teardown
        }
      }
    };
    // Rerun cleanup logic if handlers change (they shouldn't often due to useCallback)
  }, [handleGlobalMouseMove, handleMouseUp, handleGlobalTouchMove]);

  // --- Render Logic ---
  // Render container/background immediately, but overlay only when width is known
  if (overlayWidth === null) {
    // Still render the container with the ref so its width can be measured
    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
          visibility: "hidden" /* Hide until calculated */,
        }}
      >
        {/* Render background content for measurement purposes - width doesn't matter yet */}
        <div style={{ width: "100%", height: "100%" }}>{backgroundContent}</div>
      </div>
    );
  }

  // Determine container width for accurate calculation
  const containerWidth = containerRef.current?.offsetWidth ?? 0;
  // Calculate the width for the background content
  const backgroundWidth = containerWidth - overlayWidth;


  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative", // Crucial for absolute positioning of children
        overflow: "hidden", // Prevent container scrollbars
        // background:"white" // background was added in original code, keep if intended
      }}
    >
      {/* --- Background Content (Static) --- */}
      {/* Positioned from the left, with calculated width */}
      <div
          style={{
          position: "absolute",
          top: 0,
          bottom: 0, // Stretch full height
          left: 0,
          width:`${backgroundWidth - 10}px`, // <--- DYNAMIC WIDTH CALCULATION HERE
          // Setting right here is unnecessary and potentially conflicting with width/left
          // right: 'auto', // Ensure it doesn't conflict if you were using right previously
          overflow: "auto", // Allow background content to scroll if needed
          zIndex: zIndexBase -1, // Below the overlay panel and slider
          backgroundColor: 'transparent', // Or a default background color if needed
          marginRight:"20px" // Keep the margin if you want space *within* the background div
        }}
      >
        {backgroundContent}
      </div>

      {/* --- Overlay Panel (Absolutely Positioned) --- */}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0, // Anchored to the right edge
          width: `${overlayWidth}px`, // Dynamic width controlled by slider
          backgroundColor: overlayBgColor,
          boxShadow: overlayShadow, // Apply shadow
          overflow: "auto", // Scroll overlay content if needed
          zIndex: zIndexBase,
        }}
      >
        {overlayContent}
      </div>

      {/* --- Slider Handle (Absolutely Positioned next to Overlay) --- */}
      <div
        className="group flex justify-center items-center"
        // Removed hover:scale-150 class for clarity, add back if needed
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          // Position its right edge where the overlay's left edge is
          // To calculate the left edge: containerWidth - overlayWidth
          // The slider handle is positioned relative to the container's right edge,
          // which is exactly `overlayWidth` distance away from the slider's desired position.
          right: `${overlayWidth}px`,
          transform: "translateX(50%)", // Center the handle visually over the edge
          width: `${sliderWidth}px`,
          cursor: "col-resize",
          backgroundColor: sliderColor, // This colors the *draggable area*
          zIndex: zIndexBase + 1, // Above overlay panel
          userSelect: "none",
          touchAction: "none", // Improve touch handling
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
        {/* Optional visual handle content */}
        <div className="group-hover:opacity-100 select-none flex items-center justify-center bg-[#e5e5e5] rounded-full w-10 h-8 opacity-0 transform transition-opacity duration-150 cursor-pointer">
          {/* Shrink Overlay Button */}
          <div
            className="text-gray-700 opacity-65 size-full overflow-hidden hover:text-emerald-700 hover:opacity-100 flex items-center justify-center "
            onClick={() => {
              const containerWidth = containerRef.current?.offsetWidth || 0;
              // Calculate the maximum possible width allowed
              const maxWidthPx =
                maxOverlayWidthPercent != null
                  ? (containerWidth * Math.min(100, maxOverlayWidthPercent)) /
                    100
                  : containerWidth; // Max is container width if no percent limit

              setOverlayWidth(maxWidthPx); // Set overlay to max width

              if (onSlide && containerRef.current) {
                onSlide(maxWidthPx, maxWidthPx / containerWidth);
              }
            }}
          >
            {/* Rotate dropDown icon for expand/collapse look */}
            <img src={dropDown} className="w-5 min-w-5 -rotate-90" />
          </div>
          {/* Expand Overlay Button */}
          <div
            className="text-gray-700 opacity-65 size-full overflow-hidden hover:text-emerald-700 flex items-center hover:opacity-100 justify-center"
            onClick={() => {
              const safeMinWidth = minOverlayWidth ?? 50; // Use nullish coalescing
              setOverlayWidth(safeMinWidth); // Set overlay to min width

              if (onSlide && containerRef.current) {
                onSlide(
                  safeMinWidth,
                  safeMinWidth / containerRef.current.offsetWidth
                );
              }
            }}
          >
            {/* Rotate dropDown icon for expand/collapse look */}
            <img src={dropDown} className="w-5 min-w-5 rotate-90" />
          </div>
        </div>
         {/* Optional: Add a visual line handle instead of coloring the whole grab area */}
         {/* <div style={{ width: '2px', height: '100%', backgroundColor: sliderColor }}></div> */}
      </div>
    </div>
  );
};

export default OverlaySlider;